import React from 'react';
import {useController} from 'react-hook-form';
import {
    FormError,
    FormLabel,
    getInputClassNames,
    getInputWrapperClass,
    getWholeDivWrapperClass
} from "./sharedStyles.tsx";
import {CheckboxComponent, FormComponentProps, ParentController} from '../DynamicFormTypes.ts';
import {useFormMethods} from "../DyanmicFormContext.tsx";
import {useFieldBlurHandler} from "../hooks/OnFieldBlurHanlder.tsx";
import {useValidationRules} from "../hooks/useValidationRules.tsx";

function cx(...classes: (string | false | null | undefined)[]): string {
    return classes.filter(Boolean).join(' ');
}

export const CheckboxRenderer: React.FC<FormComponentProps<CheckboxComponent> & {parentController?: ParentController}> = ({
                                                                                                                              component,
                                                                                                                              parentController = undefined
}) => {
    const { formMethods } = useFormMethods()
    const validate = useValidationRules({ component });
    const { handleFieldBlur } = useFieldBlurHandler({
        component
    });

    const controller = parentController ??
        useController({
            name: component.fieldKey,
            control: formMethods.control,
            rules: validate
        });

    const { field, fieldState } = controller;
    const { onChange, onBlur, value, ref } = field;
    const checkboxSize = component.compactUI ? 'h-4 w-4' : 'h-5 w-5';
    const labelClasses = component.compactUI ? 'flex items-start gap-3 p-2 rounded-md mt-1' : 'flex items-start gap-3 p-2 rounded-md';
    const labelOverride = component.compactUI ? "text-xs" : undefined;

    // Common checkbox input
    const checkboxInput = (
        <input
            type="checkbox"
            id={component.fieldKey}
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            onBlur={(_e) => {
                onBlur();
                handleFieldBlur();
            }}
            ref={ref}
            className="sr-only peer"
        />
    );

    // Common custom checkbox visual
    const customCheckbox = (
        <div
            className={cx(
                `${checkboxSize} rounded border flex items-center justify-center mt-0.5`,
                'border-gray-300 bg-white peer-checked:border-indigo-500',
                'peer-checked:bg-indigo-500 peer-focus:ring-2 peer-focus:ring-indigo-500/20',
                fieldState.error && 'border-red-300 peer-checked:border-red-500'
            )}
        >
            <svg
                className="hidden peer-checked:block h-4 w-4 text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                    clipRule="evenodd"
                />
            </svg>
        </div>
    );

    // Common label content
    const labelContent = (
        <label
            htmlFor={component.fieldKey}
            className={cx(
                labelClasses,
                getInputClassNames(component, fieldState),
                'hover:border-indigo-500 cursor-pointer',
                value && 'ring-indigo-500/20 ring-2'
            )}
        >
            {checkboxInput}
            {customCheckbox}
            <FormLabel component={component} classOverride={labelOverride}/>
            {component.description && (
                <div className="flex flex-col">
                    <div className="text-sm text-gray-500">{component.description}</div>
                </div>
            )}
        </label>
    );

    // Render based on UI mode
    if (component.compactUI) {
        return (
            <div className={getInputWrapperClass(component)}>
                {labelContent}
                <FormError fieldState={fieldState} component={component} />
            </div>
        );
    }

    return (
        <div className={getWholeDivWrapperClass(component)}>
            <span className="ml-2"/>
            <div className={getInputWrapperClass(component)}>
                {labelContent}
            </div>
            <FormError fieldState={fieldState} component={component} />
        </div>
    );
}

export const Checkbox = React.memo(CheckboxRenderer);